import { useState, useEffect, useMemo } from 'react';
import { klientApi, klientAuth } from '../klientApi';
import { Btn } from '../components/Input';
import { IconBuilding, IconLink, IconCalendar, IconMoney, IconCheck, IconAlert, IconClock, IconUser, IconDoc, IconEye, IconPackage, IconX, IconLocation, IconChevronRight, IconSnowflake } from '../components/Icons';
import { formatCurrency, formatDate } from '../utils';

// ─── i18n (demo): URL ?lang=en przełącza wszystkie stringi panelu klienta na angielski.
// Default = pl. Bez zmian w bazie ani API — tylko warstwa UI.
const EN_DICT = {
  'Panel klienta': 'Client portal',
  'Wyloguj': 'Log out',
  'Email': 'Email',
  'Telefon': 'Phone',
  'Adres': 'Address',
  'NIP': 'VAT ID',
  'Nieprawidłowy adres email': 'Invalid email address',
  'Sprawdź swoją skrzynkę. Kod jest ważny przez 5 minut.': 'Check your inbox. The code is valid for 5 minutes.',
  'Wpisz 6-cyfrowy kod': 'Enter the 6-digit code',
  'Twój adres email': 'Your email address',
  'ty@firma.pl': 'you@company.com',
  'Wysyłam...': 'Sending…',
  'Wyślij kod logowania': 'Send login code',
  'Wpisz email zapisany w panelu fotografa. Dostaniesz 6-cyfrowy kod jednorazowy.': 'Enter the email saved by your photographer. You will receive a one-time 6-digit code.',
  'Wysłaliśmy kod na': 'We sent the code to',
  'Sprawdzam...': 'Verifying…',
  'Zaloguj': 'Log in',
  'Zmień email': 'Change email',
  'Aktywny': 'Active',
  'Zamrożony': 'Frozen',
  'Przekazany': 'Transferred',
  'Usunięty': 'Deleted',
  'Aktywne': 'Active',
  'Zamrożone': 'Frozen',
  'Przekazane': 'Transferred',
  'Usunięte': 'Deleted',
  'Zgłoś do usunięcia': 'Request removal',
  'Zgłoś ten spacer do usunięcia': 'Request this walkthrough to be removed',
  'Zgłoszono do usunięcia': 'Removal requested',
  'Cofnij zgłoszenie': 'Undo request',
  'Usunięto': 'Removed on',
  'Otwórz spacer 3D': 'Open 3D walkthrough',
  'SPACER 3D': '3D TOUR',
  'Kopiuj link do spaceru 3D': 'Copy 3D walkthrough link',
  'SKOPIOWANO': 'COPIED',
  'KOPIUJ LINK': 'COPY LINK',
  'Data wykonania': 'Date',
  'Stawka hostingu': 'Hosting fee',
  '/mies.': '/mo',
  'mies.': 'mo',
  'Okres w cenie': 'Included period',
  'Hosting opłacony do': 'Hosting paid until',
  'Symulator kosztu hostingu': 'Hosting cost simulator',
  'Wszystkie spacery': 'All walkthroughs',
  'Pojedynczy spacer': 'Single walkthrough',
  'Spacer': 'Walkthrough',
  'Brak aktywnych spacerów': 'No active walkthroughs',
  'Data docelowa': 'Target date',
  'Liczę...': 'Calculating…',
  'Oblicz': 'Calculate',
  'Koszt do': 'Cost until',
  'Pobierz raport PDF z aktualną symulacją': 'Download PDF report with current simulation',
  'Pobieram...': 'Downloading…',
  'Pobierz PDF': 'Download PDF',
  'Zamknij wynik symulacji': 'Close simulation result',
  '(nieaktywny)': '(inactive)',
  'Naliczanie od': 'Billing starts',
  'miesięcy': 'months',
  'Domyślnie': 'Default',
  'Chronologicznie': 'By date',
  'Alfabetycznie': 'Alphabetical',
  'Pojedyncze spacery': 'Individual walkthroughs',
  'Pozostałe spacery (nieaktywne)': 'Other walkthroughs (inactive)',
  'Zapłacona': 'Paid',
  'Anulowana': 'Cancelled',
  'Wystawiona': 'Issued',
  '(bez numeru)': '(no number)',
  'Pakiet': 'Package',
  'spacerów': 'walkthroughs',
  'Sprzedaż': 'Sold',
  'termin': 'due',
  'opłacona': 'paid',
  'PDF wkrótce': 'PDF soon',
  'Brak wystawionych faktur': 'No invoices yet',
  'Masz 1 nieopłaconą fakturę': 'You have 1 unpaid invoice',
  'Zwiń': 'Collapse',
  'Ładowanie...': 'Loading…',
  'Zaloguj ponownie': 'Log in again',
  'Spacery łącznie': 'Walkthroughs total',
  'Narosły hosting (dziś)': 'Accrued hosting (today)',
  'PODGLĄD FOTOGRAFA · widzisz panel tak, jak zobaczy go klient': 'PHOTOGRAPHER PREVIEW · you see the portal as the client will',
  'Zamknij podgląd': 'Close preview',
  'Faktury': 'Invoices',
  'Twoje spacery': 'Your walkthroughs',
  'Brak spacerów do wyświetlenia': 'No walkthroughs to display',
  'Nie znaleziono panelu': 'Portal not found',
  'Sprawdź czy link jest poprawny.': 'Check that the link is correct.',
};

// Język panelu: jawny ?lang=en/pl w URL ma priorytet, inaczej zapamiętany wybór klienta
// z localStorage (sp_klient_lang), inaczej pl. Wybór z URL zapisujemy, żeby trzymał się
// przy kolejnych wizytach. Po zalogowaniu KlientPanel dosynchronizowuje język z konta klienta.
const URL_LANG = (() => {
  try {
    if (typeof window === 'undefined') return null;
    const v = new URLSearchParams(window.location.search).get('lang');
    return v === 'en' || v === 'pl' ? v : null;
  } catch { return null; }
})();
const STORED_LANG = (() => {
  try { return localStorage.getItem('sp_klient_lang'); } catch { return null; }
})();
const CURRENT_LANG = URL_LANG || STORED_LANG || 'pl';
const IS_EN = CURRENT_LANG === 'en';
// Zapisz jawny wybór z URL, żeby był pamiętany przy następnej wizycie bez ?lang.
if (URL_LANG) { try { localStorage.setItem('sp_klient_lang', URL_LANG); } catch {} }

function t(s) {
  if (!IS_EN) return s;
  return EN_DICT[s] != null ? EN_DICT[s] : s;
}
// Waluta: kwoty są w złotówkach. W EN formatujemy je jako PLN (bez przeliczania kursu) — żeby nie zafałszować wartości symbolem $.
function fmtMoney(val) {
  if (IS_EN) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'PLN' }).format(val || 0);
  }
  return formatCurrency(val);
}

// Detekcja slug z URL: my.scanpilots.com/<slug> lub <host>/k/<slug> (lokalnie / fallback)
// Pozostawiamy też 'klient.' jako alias backward-compat — gdyby ktoś już udostępnił link.
export function detectKlientSlug() {
  const host = window.location.hostname;
  const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
  const segs = path.split('/').filter(Boolean);
  if (host.startsWith('my.') || host.startsWith('klient.')) {
    return segs[0] || null;
  }
  if (segs[0] === 'k' && segs[1]) {
    return segs[1];
  }
  return null;
}

// URL panelu fotografa dla konkretnego klienta — fallback, gdy karta podglądu
// nie może sama się zamknąć (np. otwarta ręcznie, nie przez window.open).
function fotografKlientUrl(klientId) {
  const host = window.location.hostname;
  // Portal klienta jest na my./klient. — panel fotografa na app.scanpilots.com.
  const base = (host === 'my.scanpilots.com' || host === 'klient.scanpilots.com')
    ? 'https://app.scanpilots.com'
    : window.location.origin; // lokalnie / staging: ten sam origin
  return klientId ? `${base}/?klient=${klientId}` : `${base}/`;
}

// Zamknij podgląd fotografa: wyczyść sesję podglądu, zamknij kartę (otwartą przez
// window.open w panelu fotografa), a gdyby się nie dało — wróć do panelu klienta.
function closeFotografPreview(klientId) {
  klientAuth.logout();
  sessionStorage.removeItem('sp_klient_preview');
  window.close();
  // Jeśli karta nadal istnieje (np. otwarta ręcznie) — nawiguj do panelu fotografa.
  setTimeout(() => {
    window.location.href = fotografKlientUrl(klientId);
  }, 200);
}

// ─── Footer (dane fotografa) ─────────────────────────────────────────────────
function Footer({ fotograf }) {
  if (!fotograf) return null;
  const nazwa = fotograf.firma || fotograf.imie || '';
  return (
    <footer style={{
      borderTop: '1px solid var(--border)',
      padding: '24px 20px',
      marginTop: 'auto',
      background: 'var(--bg-nav)',
    }}>
      <div style={{ maxWidth: '960px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: '24px', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink)', marginBottom: '4px' }}>{nazwa}</p>
          {fotograf.adres && <p style={{ fontSize: '10px', color: 'var(--ink3)', marginBottom: '2px' }}>{fotograf.adres}</p>}
          {fotograf.nip && <p style={{ fontSize: '10px', color: 'var(--ink3)', marginBottom: '2px' }}>NIP: {fotograf.nip}</p>}
        </div>
        <div style={{ textAlign: 'right' }}>
          {fotograf.email && <p style={{ fontSize: '10px', color: 'var(--ink3)' }}>{fotograf.email}</p>}
          {fotograf.telefon && <p style={{ fontSize: '10px', color: 'var(--ink3)' }}>{fotograf.telefon}</p>}
          {fotograf.strona_www && <p style={{ fontSize: '10px', color: 'var(--ink3)' }}>{fotograf.strona_www}</p>}
        </div>
      </div>
      <p style={{ textAlign: 'center', fontSize: '9px', color: 'var(--ink3)', marginTop: '20px', letterSpacing: '0.04em' }}>
        Panel klienta · powered by ScanPilots
      </p>
    </footer>
  );
}

// ─── Header (dane klienta) ───────────────────────────────────────────────────
function Header({ klient, onLogout }) {
  const nazwa = klient?.imie_nazwisko || klient?.nazwa || '';
  const firma = klient?.nazwa_firmy;
  const initials = (nazwa || firma || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();

  const meta = [];
  if (klient?.email) meta.push({ label: t('Email'), value: klient.email });
  if (klient?.telefon) meta.push({ label: t('Telefon'), value: klient.telefon });
  if (klient?.adres) meta.push({ label: t('Adres'), value: klient.adres, icon: IconLocation });
  if (klient?.nip) meta.push({ label: t('NIP'), value: klient.nip });

  return (
    <header style={{
      borderBottom: '1px solid var(--border)',
      padding: '20px 20px',
      background: 'var(--bg-nav)',
    }}>
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '50%',
              background: 'var(--bg4)', border: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--acc2)', fontSize: '15px', fontWeight: 800, flexShrink: 0,
              letterSpacing: '0.02em',
            }}>
              {initials || <IconUser className="w-5 h-5" />}
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: '16px', fontWeight: 800, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nazwa || firma}</p>
              {firma && nazwa && <p style={{ fontSize: '11px', color: 'var(--ink3)', marginTop: '1px' }}>{firma}</p>}
              <p style={{ fontSize: '9px', color: 'var(--ink3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: '3px' }}>
                {t('Panel klienta')}
              </p>
            </div>
          </div>
          <Btn variant="ghost" onClick={onLogout}>{t('Wyloguj')}</Btn>
        </div>

        {meta.length > 0 && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '8px 24px',
            marginTop: '14px', paddingTop: '14px', borderTop: '1px solid var(--border)',
          }}>
            {meta.map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
                  {Icon && <Icon className="w-3 h-3" style={{ color: 'var(--ink3)', flexShrink: 0 }} />}
                  <span style={{ fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    {m.label}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--ink2)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.value}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </header>
  );
}

// ─── Strona logowania ────────────────────────────────────────────────────────
function KlientLogin({ slug, branding, onLoggedIn }) {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');

  const fotografNazwa = branding?.fotograf?.firma || branding?.fotograf?.imie || t('Panel klienta');

  const submitEmail = async (e) => {
    e?.preventDefault();
    setErr(''); setInfo('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr(t('Nieprawidłowy adres email'));
      return;
    }
    setLoading(true);
    try {
      await klientApi.login(slug, email);
      setInfo(t('Sprawdź swoją skrzynkę. Kod jest ważny przez 5 minut.'));
      setStep('code');
    } catch (e) {
      setErr(e.message);
    } finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e?.preventDefault();
    setErr('');
    if (!/^\d{6}$/.test(code)) {
      setErr(t('Wpisz 6-cyfrowy kod'));
      return;
    }
    setLoading(true);
    try {
      const { token } = await klientApi.verify(slug, email, code);
      klientAuth.token = token;
      onLoggedIn();
    } catch (e) {
      setErr(e.message);
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
        <div className="ds-card" style={{ width: '100%', maxWidth: '380px', padding: '32px 28px' }}>
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <p style={{ fontSize: '10px', color: 'var(--ink3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '4px' }}>
              {t('Panel klienta')}
            </p>
            <h1 style={{ fontSize: '20px', fontWeight: 800, color: 'var(--acc2)' }}>{fotografNazwa}</h1>
          </div>

          {step === 'email' && (
            <form onSubmit={submitEmail}>
              <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: 'var(--ink2)', letterSpacing: '0.04em', marginBottom: '6px' }}>
                {t('Twój adres email')}
              </label>
              <input
                className="ds-input"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('ty@firma.pl')}
                autoFocus
                disabled={loading}
                style={{ marginBottom: '14px' }}
              />
              {err && <p style={{ fontSize: '11px', color: 'var(--err2)', marginBottom: '12px' }}>{err}</p>}
              <Btn variant="primary" type="submit" disabled={loading} style={{ width: '100%', padding: '10px' }}>
                {loading ? t('Wysyłam...') : t('Wyślij kod logowania')}
              </Btn>
              <p style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '16px', textAlign: 'center', lineHeight: 1.6 }}>
                {t('Wpisz email zapisany w panelu fotografa. Dostaniesz 6-cyfrowy kod jednorazowy.')}
              </p>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={submitCode}>
              <p style={{ fontSize: '11px', color: 'var(--ink2)', marginBottom: '12px' }}>
                {t('Wysłaliśmy kod na')} <strong>{email}</strong>
              </p>
              {info && <p style={{ fontSize: '11px', color: 'var(--ok2)', marginBottom: '12px' }}>{info}</p>}
              <input
                className="ds-input"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                autoFocus
                disabled={loading}
                style={{ marginBottom: '14px', fontSize: '20px', textAlign: 'center', letterSpacing: '8px', fontFamily: 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace' }}
              />
              {err && <p style={{ fontSize: '11px', color: 'var(--err2)', marginBottom: '12px' }}>{err}</p>}
              <Btn variant="primary" type="submit" disabled={loading} style={{ width: '100%', padding: '10px' }}>
                {loading ? t('Sprawdzam...') : t('Zaloguj')}
              </Btn>
              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); setErr(''); setInfo(''); }}
                style={{ width: '100%', marginTop: '12px', background: 'none', border: 'none', color: 'var(--ink3)', fontSize: '10px', cursor: 'pointer', textDecoration: 'underline' }}
              >
                {t('Zmień email')}
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer fotograf={branding?.fotograf} />
    </div>
  );
}

// ─── Karta spaceru ───────────────────────────────────────────────────────────
function SpacerCard({ s, onSelect, selected, onRequestDeletion }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const statusMeta = {
    aktywny:    { label: t('Aktywny'),    cls: 'pill-ok' },
    zamrozony:  { label: t('Zamrożony'),  cls: 'pill-warn' },
    przekazany: { label: t('Przekazany'), cls: 'pill-warn' },
    usuniety:   { label: t('Usunięty'),   cls: 'pill-err' },
  }[s.status] || { label: s.status, cls: 'pill-warn' };
  const zgloszony = !!s.klient_zglosil_usuniecie;

  const copyLink = async (e) => {
    e.stopPropagation();
    if (!s.link_matterport) return;
    try {
      await navigator.clipboard.writeText(s.link_matterport);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {}
  };

  const showImage = s.miniatura && !imgFailed;

  return (
    <div
      className="ds-card"
      onClick={onSelect}
      style={{
        padding: 0,
        cursor: onSelect ? 'pointer' : 'default',
        outline: selected ? '2px solid var(--acc)' : 'none',
        transition: 'outline-color 0.15s',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Podgląd 16:9 — miniatura albo fallback z ikoną */}
      <div style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        background: 'var(--bg3)',
        borderBottom: '1px solid var(--border)',
        overflow: 'hidden',
      }}>
        {showImage && (
          <img
            src={s.miniatura}
            alt={s.nazwa}
            loading="lazy"
            decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={() => setImgFailed(true)}
          />
        )}
        {!showImage && (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink3)' }}>
            <IconBuilding className="w-8 h-8" />
          </div>
        )}
        {s.link_matterport && (
          <a
            href={s.link_matterport}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            title={t('Otwórz spacer 3D')}
            style={{
              position: 'absolute',
              right: '10px',
              bottom: '10px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              background: 'rgba(0,0,0,0.65)',
              backdropFilter: 'blur(6px)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '999px',
              color: '#fff',
              fontSize: '9px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textDecoration: 'none',
            }}
          >
            <IconLink className="w-3 h-3" /> {t('SPACER 3D')}
          </a>
        )}
      </div>

      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink)', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {s.nazwa}
          </h3>
          {s.adres && (
            <p style={{ fontSize: '10px', color: 'var(--ink3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {s.adres}
            </p>
          )}
        </div>
        <span className={`pill ${statusMeta.cls}`}>{statusMeta.label}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', fontSize: '10px' }}>
        <div>
          <p style={{ color: 'var(--ink3)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '2px', fontSize: '9px' }}>
            {t('Data wykonania')}
          </p>
          <p style={{ color: 'var(--ink)', fontWeight: 600 }}>{formatDate(s.data_wykonania)}</p>
        </div>
        <div>
          <p style={{ color: 'var(--ink3)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '2px', fontSize: '9px' }}>
            {t('Stawka hostingu')}
          </p>
          <p style={{ color: 'var(--ink)', fontWeight: 600 }}>{fmtMoney(s.stawka_hostingu)}{t('/mies.')}</p>
        </div>
        <div>
          <p style={{ color: 'var(--ink3)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '2px', fontSize: '9px' }}>
            {t('Okres w cenie')}
          </p>
          <p style={{ color: 'var(--ink)', fontWeight: 600 }}>{s.darmowy_okres} {t('mies.')}</p>
        </div>
        <div>
          <p style={{ color: 'var(--ink3)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '2px', fontSize: '9px' }}>
            {t('Hosting opłacony do')}
          </p>
          <p style={{ color: 'var(--ink)', fontWeight: 600 }}>
            {s.rozliczony_do ? formatDate(s.rozliczony_do) : formatDate(s.koniec_darmowego)}
          </p>
        </div>
      </div>

      {s.link_matterport && (
        <button
          onClick={copyLink}
          title={t('Kopiuj link do spaceru 3D')}
          style={{
            marginTop: '12px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            padding: '8px 12px',
            background: linkCopied ? 'rgba(90,158,114,0.18)' : 'var(--bg4)',
            border: linkCopied ? '1px solid rgba(90,158,114,0.35)' : '1px solid var(--border)',
            borderRadius: 'var(--r-btn)',
            color: linkCopied ? 'var(--ok2)' : 'var(--ink2)',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            cursor: 'pointer',
            transition: 'all 0.15s',
            alignSelf: 'flex-start',
          }}
        >
          {linkCopied ? <><IconCheck className="w-3 h-3" /> {t('SKOPIOWANO')}</> : <><IconLink className="w-3 h-3" /> {t('KOPIUJ LINK')}</>}
        </button>
      )}

      {/* Zgłoszenie do usunięcia — tylko dla aktywnych spacerów */}
      {onRequestDeletion && s.status === 'aktywny' && (
        <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
          {zgloszony ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px', fontWeight: 700, color: 'var(--acc2)' }}>
                <IconClock className="w-3 h-3" /> {t('Zgłoszono do usunięcia')}
              </span>
              <button
                onClick={e => { e.stopPropagation(); onRequestDeletion(s, false); }}
                style={{ background: 'none', border: 'none', color: 'var(--ink3)', fontSize: '10px', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
              >
                {t('Cofnij zgłoszenie')}
              </button>
            </div>
          ) : (
            <button
              onClick={e => { e.stopPropagation(); onRequestDeletion(s, true); }}
              title={t('Zgłoś ten spacer do usunięcia')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '8px 12px', background: 'none',
                border: '1px solid var(--border)', borderRadius: 'var(--r-btn)',
                color: 'var(--ink3)', fontSize: '10px', fontWeight: 700,
                letterSpacing: '0.04em', cursor: 'pointer', alignSelf: 'flex-start',
              }}
            >
              <IconX className="w-3 h-3" /> {t('Zgłoś do usunięcia')}
            </button>
          )}
        </div>
      )}

      </div>
    </div>
  );
}

// ─── Symulator hostingu ──────────────────────────────────────────────────────
function Simulator({ spacery }) {
  const today = new Date().toISOString().slice(0, 10);
  const defaultDate = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 12);
    return d.toISOString().slice(0, 10);
  }, []);

  const [mode, setMode] = useState('all'); // 'all' | 'one'
  const [spacerId, setSpacerId] = useState(spacery[0]?.id || null);
  const [date, setDate] = useState(defaultDate);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [downloading, setDownloading] = useState(false);

  const aktywne = spacery.filter(s => s.status === 'aktywny');

  useEffect(() => {
    if (mode === 'one' && !aktywne.find(s => s.id === spacerId) && aktywne[0]) {
      setSpacerId(aktywne[0].id);
    }
  }, [mode, spacerId, aktywne]);

  const run = async () => {
    setErr(''); setLoading(true);
    try {
      const r = await klientApi.simulate(date, mode === 'one' ? spacerId : null);
      setResult(r);
    } catch (e) {
      setErr(e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="ds-card" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <IconMoney className="w-4 h-4" style={{ color: 'var(--acc2)' }} />
        <h2 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          {t('Symulator kosztu hostingu')}
        </h2>
      </div>

      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
        <button
          onClick={() => setMode('all')}
          className={mode === 'all' ? 'pill pill-warn' : 'pill'}
          style={{ cursor: 'pointer', padding: '6px 12px', fontSize: '10px', border: mode === 'all' ? undefined : '1px solid var(--border)', background: mode === 'all' ? undefined : 'var(--bg4)', color: mode === 'all' ? undefined : 'var(--ink2)' }}
        >
          {t('Wszystkie spacery')}
        </button>
        <button
          onClick={() => setMode('one')}
          className={mode === 'one' ? 'pill pill-warn' : 'pill'}
          style={{ cursor: 'pointer', padding: '6px 12px', fontSize: '10px', border: mode === 'one' ? undefined : '1px solid var(--border)', background: mode === 'one' ? undefined : 'var(--bg4)', color: mode === 'one' ? undefined : 'var(--ink2)' }}
        >
          {t('Pojedynczy spacer')}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: mode === 'one' ? '1fr 1fr auto' : '1fr auto', gap: '8px', alignItems: 'end' }}>
        {mode === 'one' && (
          <div>
            <label style={{ display: 'block', fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '4px' }}>
              {t('Spacer')}
            </label>
            <select
              className="ds-input"
              value={spacerId || ''}
              onChange={e => setSpacerId(Number(e.target.value))}
            >
              {aktywne.length === 0 && <option value="">{t('Brak aktywnych spacerów')}</option>}
              {aktywne.map(s => <option key={s.id} value={s.id}>{s.nazwa}</option>)}
            </select>
          </div>
        )}
        <div>
          <label style={{ display: 'block', fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '4px' }}>
            {t('Data docelowa')}
          </label>
          <input
            type="date"
            className="ds-input"
            value={date}
            min={today}
            onChange={e => setDate(e.target.value)}
          />
        </div>
        <Btn variant="primary" onClick={run} disabled={loading || (mode === 'one' && !spacerId)} style={{ padding: '8px 16px' }}>
          {loading ? t('Liczę...') : t('Oblicz')}
        </Btn>
      </div>

      {err && <p style={{ fontSize: '11px', color: 'var(--err2)', marginTop: '12px' }}>{err}</p>}

      {result && (
        <div style={{ marginTop: '18px', padding: '16px', background: 'var(--bg4)', borderRadius: '8px', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '14px' }}>
            <div style={{ flex: 1, minWidth: '180px' }}>
              <p style={{ fontSize: '10px', color: 'var(--ink3)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {t('Koszt do')} {formatDate(result.do)}
              </p>
              <p style={{ fontSize: '22px', fontWeight: 800, color: 'var(--acc2)' }}>
                {fmtMoney(result.total)}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Btn
                variant="ghost"
                disabled={downloading}
                onClick={async () => {
                  setErr(''); setDownloading(true);
                  try {
                    await klientApi.downloadRaport({
                      symulacja_data: date,
                      spacer_id: mode === 'one' ? spacerId : null,
                    });
                  } catch (e) {
                    setErr(e.message);
                  } finally { setDownloading(false); }
                }}
                title={t('Pobierz raport PDF z aktualną symulacją')}
              >
                <span className="flex items-center gap-1.5">
                  <IconDoc className="w-3 h-3" /> {downloading ? t('Pobieram...') : t('Pobierz PDF')}
                </span>
              </Btn>
              <button
                onClick={() => { setResult(null); setErr(''); }}
                title={t('Zamknij wynik symulacji')}
                aria-label={t('Zamknij wynik symulacji')}
                style={{
                  width: '30px', height: '30px', flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--bg3)', border: '1px solid var(--border)',
                  borderRadius: 'var(--r-btn)', color: 'var(--ink3)', cursor: 'pointer',
                }}
              >
                <IconX className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          {result.items.length > 1 && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
              {result.items.map(it => (
                <div key={it.spacer_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', padding: '4px 0' }}>
                  <span style={{ color: 'var(--ink2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
                    {it.nazwa}
                    {!it.aktywny && <span style={{ fontSize: '9px', color: 'var(--ink3)', marginLeft: '6px' }}>{t('(nieaktywny)')}</span>}
                  </span>
                  <span style={{ color: 'var(--ink)', fontWeight: 600 }}>
                    {it.miesiace > 0 ? `${it.miesiace} ${t('mies.')} ×  ${fmtMoney(it.stawka || 0)} = ${fmtMoney(it.kwota)}` : fmtMoney(0)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {result.items.length === 1 && result.items[0].od && (
            <p style={{ fontSize: '10px', color: 'var(--ink3)', marginTop: '8px' }}>
              {t('Naliczanie od')} {formatDate(result.items[0].od)} · {result.items[0].miesiace} {t('miesięcy')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sekcja spacerów: filtr sortowania + grupowanie po pakietach ─────────────
const spacerAktywny = (s) => s.status === 'aktywny';
const byDateDesc = (a, b) => String(b.data_wykonania || '').localeCompare(String(a.data_wykonania || ''));
const byNazwa = (a, b) => String(a.nazwa || '').localeCompare(String(b.nazwa || ''), 'pl', { sensitivity: 'base' });

function CardGrid({ items, onRequestDeletion }) {
  return (
    <div className="kp-spacery-grid">
      {items.map(s => <SpacerCard key={s.id} s={s} onRequestDeletion={onRequestDeletion} />)}
    </div>
  );
}

// Uproszczona lista spacerów nieaktywnych — bez miniatury i linku,
// tylko nazwa, data wykonania i (jeśli jest) data usunięcia.
function NieaktywneList({ items }) {
  return (
    <div className="ds-card" style={{ padding: 0, overflow: 'hidden' }}>
      {items.map((s, i) => (
        <div
          key={s.id}
          style={{
            display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
            padding: '12px 16px',
            borderBottom: i === items.length - 1 ? 'none' : '1px solid var(--border)',
          }}
        >
          <span style={{ flex: '1 1 180px', minWidth: 0, fontSize: '13px', fontWeight: 600, color: 'var(--ink2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {s.nazwa}
          </span>
          <span style={{ fontSize: '10px', color: 'var(--ink3)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <IconCalendar className="w-3 h-3" /> {formatDate(s.data_wykonania)}
          </span>
          {s.data_usuniecia && (
            <span style={{ fontSize: '10px', color: 'var(--err2)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <IconX className="w-3 h-3" /> {t('Usunięto')} {formatDate(s.data_usuniecia)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function GroupHeader({ icon: Icon, label, count, muted }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
      <Icon className="w-3.5 h-3.5" style={{ color: muted ? 'var(--ink3)' : 'var(--acc2)' }} />
      <h3 style={{
        fontSize: '11px', fontWeight: 700,
        color: muted ? 'var(--ink3)' : 'var(--ink)',
        letterSpacing: '0.06em', textTransform: 'uppercase',
      }}>
        {label}
      </h3>
      <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>· {count}</span>
    </div>
  );
}

function SpacerySection({ spacery, onRequestDeletion }) {
  const [sort, setSort] = useState('default'); // 'default' | 'chrono' | 'alpha'

  const aktywne = useMemo(() => spacery.filter(spacerAktywny), [spacery]);
  const nieaktywne = useMemo(() => spacery.filter(s => !spacerAktywny(s)), [spacery]);

  // Domyślnie: pakiety (od najnowszego) → pojedyncze spacery → na końcu nieaktywne.
  const groups = useMemo(() => {
    const map = new Map();
    for (const s of aktywne) {
      const key = s.pakiet_id || '__none__';
      if (!map.has(key)) map.set(key, { id: s.pakiet_id, nazwa: s.pakiet_nazwa, items: [] });
      map.get(key).items.push(s);
    }
    const arr = [...map.values()];
    arr.forEach(g => g.items.sort(byDateDesc));
    arr.forEach(g => { g.newest = g.items.reduce((m, s) => (String(s.data_wykonania || '') > m ? String(s.data_wykonania || '') : m), ''); });
    return arr.sort((a, b) => {
      if (a.id && !b.id) return -1;   // pakiety przed pojedynczymi
      if (!a.id && b.id) return 1;
      if (a.id && b.id) return b.newest.localeCompare(a.newest); // pakiety: najnowszy pierwszy
      return 0;
    });
  }, [aktywne]);

  const hasAnyPakiet = groups.some(g => g.id);
  const sortFn = sort === 'alpha' ? byNazwa : byDateDesc;

  const sortOptions = [
    { id: 'default', label: t('Domyślnie') },
    { id: 'chrono', label: t('Chronologicznie') },
    { id: 'alpha', label: t('Alfabetycznie') },
  ];

  const renderActive = () => {
    if (aktywne.length === 0) return null;
    if (sort !== 'default' || !hasAnyPakiet) {
      return <CardGrid items={[...aktywne].sort(sortFn)} onRequestDeletion={onRequestDeletion} />;
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {groups.map(g => (
          <div key={g.id || 'none'}>
            <GroupHeader
              icon={IconPackage}
              label={g.id ? g.nazwa : t('Pojedyncze spacery')}
              count={g.items.length}
              muted={!g.id}
            />
            <CardGrid items={g.items} onRequestDeletion={onRequestDeletion} />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <span style={{ fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', letterSpacing: '0.05em', textTransform: 'uppercase', marginRight: '4px' }}>
          Sortuj
        </span>
        {sortOptions.map(o => {
          const active = sort === o.id;
          return (
            <button
              key={o.id}
              onClick={() => setSort(o.id)}
              className={active ? 'pill pill-warn' : 'pill'}
              style={{
                cursor: 'pointer', padding: '6px 12px', fontSize: '10px',
                border: active ? undefined : '1px solid var(--border)',
                background: active ? undefined : 'var(--bg4)',
                color: active ? undefined : 'var(--ink2)',
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {renderActive()}

        {nieaktywne.length > 0 && (
          <div>
            <GroupHeader
              icon={IconSnowflake}
              label={t('Pozostałe spacery (nieaktywne)')}
              count={nieaktywne.length}
              muted
            />
            <NieaktywneList items={[...nieaktywne].sort(sort === 'alpha' ? byNazwa : byDateDesc)} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Lista faktur ────────────────────────────────────────────────────────────
const fakturaNieZaplacona = (f) => f.status !== 'zaplacona' && f.status !== 'anulowana';

function FakturaRow({ f, last, downloadingId, onDownload }) {
  const isPakiet = f.typ === 'pakiet';
  const statusMeta = f.status === 'zaplacona'
    ? { label: t('Zapłacona'), cls: 'pill-ok' }
    : f.status === 'anulowana'
    ? { label: t('Anulowana'), cls: 'pill-err' }
    : { label: t('Wystawiona'), cls: 'pill-warn' };
  return (
    <div
      style={{
        padding: '14px 16px',
        borderBottom: last ? 'none' : '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)' }}>
            {f.nr_faktury || <span style={{ color: 'var(--ink3)', fontStyle: 'italic', fontWeight: 400 }}>{t('(bez numeru)')}</span>}
          </span>
          <span className={`pill ${statusMeta.cls}`}>{statusMeta.label}</span>
          {isPakiet && (
            <span className="pill" style={{ background: 'var(--bg4)', color: 'var(--ink2)' }}>
              {t('Pakiet')}
            </span>
          )}
        </div>
        {isPakiet && (
          <p style={{ fontSize: '11px', color: 'var(--ink2)', fontWeight: 600, marginBottom: '2px' }}>
            {f.nazwa}
            {f.liczba_spacerow ? <span style={{ color: 'var(--ink3)', fontWeight: 400 }}> · {f.liczba_spacerow} {t('spacerów')}</span> : null}
          </p>
        )}
        <p style={{ fontSize: '10px', color: 'var(--ink3)' }}>
          {isPakiet ? t('Sprzedaż') : t('Wystawiona')} {formatDate(f.data_wystawienia)}
          {f.termin_platnosci && <> · {t('termin')} {formatDate(f.termin_platnosci)}</>}
          {f.data_wplaty && <> · {t('opłacona')} {formatDate(f.data_wplaty)}</>}
        </p>
      </div>
      <p style={{ fontSize: '14px', fontWeight: 800, color: 'var(--ink)', fontFamily: 'monospace' }}>
        {fmtMoney(f.kwota)}
      </p>
      {f.has_pdf ? (
        <Btn variant="primary" onClick={() => onDownload(f)} disabled={downloadingId === f.id}>
          <span className="flex items-center gap-1.5">
            <IconDoc className="w-3 h-3" /> {downloadingId === f.id ? t('Pobieram...') : t('Pobierz PDF')}
          </span>
        </Btn>
      ) : (
        <span style={{ fontSize: '10px', color: 'var(--ink3)', fontStyle: 'italic' }}>{t('PDF wkrótce')}</span>
      )}
    </div>
  );
}

function FakturyList({ faktury }) {
  const [downloadingId, setDownloadingId] = useState(null);
  const [err, setErr] = useState('');
  // Domyślnie rozwinięte, gdy są niezapłacone faktury — żeby klient od razu je widział.
  const [expanded, setExpanded] = useState(() => faktury.some(fakturaNieZaplacona));

  const download = async (f) => {
    setErr(''); setDownloadingId(f.id);
    try {
      if (f.typ === 'pakiet') {
        // f.id ma postać "pakiet-<n>" — wyciągnij numeryczny id pakietu.
        const numericId = String(f.id).replace(/^pakiet-/, '');
        await klientApi.downloadPakietPdf(numericId, f.pdf_filename);
      } else {
        await klientApi.downloadFakturaPdf(f.id, f.pdf_filename);
      }
    } catch (e) { setErr(e.message); }
    finally { setDownloadingId(null); }
  };

  if (faktury.length === 0) {
    return (
      <div className="ds-card" style={{ padding: '32px 20px', textAlign: 'center' }}>
        <IconDoc className="w-8 h-8" style={{ color: 'var(--ink3)', margin: '0 auto 12px' }} />
        <p style={{ fontSize: '12px', color: 'var(--ink2)' }}>{t('Brak wystawionych faktur')}</p>
      </div>
    );
  }

  // Faktury są posortowane od najnowszej. Zwinięte = tylko ostatnia.
  // Gdy są niezapłacone, lista startuje rozwinięta, ale klient nadal może ją zwinąć.
  const unpaidCount = faktury.filter(fakturaNieZaplacona).length;
  const showAll = expanded;
  const visible = showAll ? faktury : faktury.slice(0, 1);
  const hiddenCount = faktury.length - visible.length;

  return (
    <div className="ds-card" style={{ overflow: 'hidden' }}>
      {err && <p style={{ fontSize: '11px', color: 'var(--err2)', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>{err}</p>}
      {unpaidCount > 0 && (
        <div style={{
          padding: '10px 16px', borderBottom: '1px solid var(--border)',
          background: 'rgba(193,116,72,0.08)',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <IconAlert className="w-3.5 h-3.5" style={{ color: 'var(--err2)', flexShrink: 0 }} />
          <span style={{ fontSize: '11px', color: 'var(--ink2)', fontWeight: 600 }}>
            {unpaidCount === 1
              ? t('Masz 1 nieopłaconą fakturę')
              : (IS_EN ? `You have ${unpaidCount} unpaid invoices` : `Masz ${unpaidCount} nieopłacone faktury`)}.
          </span>
        </div>
      )}
      {visible.map((f, idx) => (
        <FakturaRow
          key={f.id}
          f={f}
          last={idx === visible.length - 1 && hiddenCount === 0}
          downloadingId={downloadingId}
          onDownload={download}
        />
      ))}
      {faktury.length > 1 && (
        <button
          onClick={() => setExpanded(e => !e)}
          style={{
            width: '100%', padding: '11px 16px', background: 'var(--bg3)', border: 'none',
            borderTop: '1px solid var(--border)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            color: 'var(--ink2)', fontSize: '11px', fontWeight: 700, letterSpacing: '0.03em',
          }}
        >
          <IconChevronRight
            className="w-3.5 h-3.5"
            style={{ transform: showAll ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.15s' }}
          />
          {showAll ? t('Zwiń') : (IS_EN ? `Show remaining invoices (${hiddenCount})` : `Pokaż pozostałe faktury (${hiddenCount})`)}
        </button>
      )}
    </div>
  );
}

// ─── Panel główny ────────────────────────────────────────────────────────────
function KlientPanel({ onLogout, previewMode }) {
  const [me, setMe] = useState(null);
  const [spacery, setSpacery] = useState([]);
  const [faktury, setFaktury] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([klientApi.me(), klientApi.spacery(), klientApi.faktury()])
      .then(([m, s, f]) => { setMe(m); setSpacery(s); setFaktury(f); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Klient zgłasza (lub cofa) spacer do usunięcia. Optymistyczna aktualizacja stanu.
  // Zapisujemy też w trybie podglądu fotografa (żeby flaga pojawiła się w CRM),
  // ale w podglądzie serwer pomija wysyłkę maila — patrz `preview` niżej.
  const handleRequestDeletion = (s, zglos) => {
    setSpacery(prev => prev.map(x => x.id === s.id
      ? { ...x, klient_zglosil_usuniecie: zglos ? new Date().toISOString() : null }
      : x));
    klientApi.zglosUsuniecie(s.id, zglos, previewMode).catch(() => {
      // Wycofaj zmianę w razie błędu
      setSpacery(prev => prev.map(x => x.id === s.id
        ? { ...x, klient_zglosil_usuniecie: zglos ? null : new Date().toISOString() }
        : x));
    });
  };

  // Synchronizacja języka klienta:
  // - jeśli w URL był jawny ?lang, zapisz go na konto klienta (utrwalenie między urządzeniami),
  // - w przeciwnym razie zastosuj język zapisany na koncie (ustawiony przez fotografa),
  //   zapisując go w localStorage i przeładowując raz, gdy różni się od aktualnie użytego.
  useEffect(() => {
    const klientLang = me?.klient?.lang;
    if (previewMode) return;
    if (URL_LANG) {
      if (klientLang && klientLang !== URL_LANG) klientApi.setLang(URL_LANG).catch(() => {});
      return;
    }
    if (!klientLang) return;
    try { localStorage.setItem('sp_klient_lang', klientLang); } catch {}
    if ((klientLang === 'en') !== IS_EN) window.location.reload();
  }, [me, previewMode]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '32px', height: '32px',
            border: '3px solid var(--border)', borderTopColor: 'var(--acc)',
            borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            margin: '0 auto 12px',
          }} />
          <p style={{ fontSize: '11px', color: 'var(--ink3)' }}>{t('Ładowanie...')}</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (err) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: '20px' }}>
        <div className="ds-card" style={{ padding: '24px', maxWidth: '400px', textAlign: 'center' }}>
          <IconAlert className="w-6 h-6" style={{ color: 'var(--err2)', margin: '0 auto 12px' }} />
          <p style={{ fontSize: '13px', color: 'var(--ink)', marginBottom: '16px' }}>{err}</p>
          <Btn variant="primary" onClick={onLogout}>{t('Zaloguj ponownie')}</Btn>
        </div>
      </div>
    );
  }

  const totalNarosle = spacery.reduce((s, sp) => s + (sp.kwota_narosla || 0), 0);

  // Liczniki per-status — pokazujemy tylko te, które faktycznie występują.
  const STATUS_META = [
    { key: 'aktywny',    label: t('Aktywne'),    icon: IconCheck,      color: 'var(--ok2)' },
    { key: 'zamrozony',  label: t('Zamrożone'),  icon: IconSnowflake,  color: 'var(--acc2)' },
    { key: 'przekazany', label: t('Przekazane'), icon: IconUser,       color: 'var(--ink2)' },
    { key: 'usuniety',   label: t('Usunięte'),   icon: IconX,          color: 'var(--err2)' },
  ];
  const statusCounts = STATUS_META
    .map(m => ({ ...m, count: spacery.filter(s => s.status === m.key).length }))
    .filter(m => m.count > 0);

  // W trybie podglądu fotografa "wyjście" = zamknięcie karty i powrót do panelu
  // fotografa dla tego klienta. W trybie klienta = zwykłe wylogowanie.
  const closeAction = previewMode
    ? () => closeFotografPreview(me?.klient?.id)
    : onLogout;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      {previewMode && (
        <div style={{
          background: '#7c3aed',
          color: '#fff',
          padding: '8px 16px',
          fontSize: '11px',
          fontWeight: 600,
          textAlign: 'center',
          letterSpacing: '0.04em',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}>
          <IconEye className="w-3.5 h-3.5" />
          {t('PODGLĄD FOTOGRAFA · widzisz panel tak, jak zobaczy go klient')}
          <button
            onClick={closeAction}
            style={{ marginLeft: '8px', background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '5px', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '3px 10px', cursor: 'pointer' }}
          >
            {t('Zamknij podgląd')}
          </button>
        </div>
      )}
      <Header klient={me.klient} onLogout={closeAction} />

      <main style={{ flex: 1, padding: '24px 16px', maxWidth: '960px', margin: '0 auto', width: '100%' }}>
        {/* Statystyki */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '12px' }}>
          <div className="ds-card" style={{ padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <IconBuilding className="w-3 h-3" style={{ color: 'var(--ink3)' }} />
              <p style={{ fontSize: '9px', color: 'var(--ink3)', letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 700 }}>
                {t('Spacery łącznie')}
              </p>
            </div>
            <p style={{ fontSize: '22px', fontWeight: 800, color: 'var(--ink)' }}>{spacery.length}</p>
          </div>
          <div className="ds-card" style={{ padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <IconClock className="w-3 h-3" style={{ color: 'var(--acc2)' }} />
              <p style={{ fontSize: '9px', color: 'var(--ink3)', letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 700 }}>
                {t('Narosły hosting (dziś)')}
              </p>
            </div>
            <p style={{ fontSize: '22px', fontWeight: 800, color: 'var(--acc2)' }}>{fmtMoney(totalNarosle)}</p>
          </div>
        </div>

        {/* Liczniki per-status — tylko statusy, które występują */}
        {statusCounts.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '24px' }}>
            {statusCounts.map(m => {
              const Icon = m.icon;
              return (
                <div key={m.key} className="ds-card" style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '30px', height: '30px', borderRadius: '7px', flexShrink: 0,
                    background: 'var(--bg4)', border: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: m.color,
                  }}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: '20px', fontWeight: 800, color: m.color, lineHeight: 1 }}>{m.count}</p>
                    <p style={{ fontSize: '9px', color: 'var(--ink3)', letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 700, marginTop: '3px' }}>
                      {m.label}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Symulator */}
        <div style={{ marginBottom: '24px' }}>
          <Simulator spacery={spacery} />
        </div>

        {/* Faktury */}
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <IconDoc className="w-4 h-4" style={{ color: 'var(--acc2)' }} />
          <h2 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {t('Faktury')}
          </h2>
        </div>
        <div style={{ marginBottom: '24px' }}>
          <FakturyList faktury={faktury} />
        </div>

        {/* Lista spacerów */}
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <IconCalendar className="w-4 h-4" style={{ color: 'var(--acc2)' }} />
          <h2 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {t('Twoje spacery')}
          </h2>
        </div>

        {spacery.length === 0 ? (
          <div className="ds-card" style={{ padding: '40px 20px', textAlign: 'center' }}>
            <IconBuilding className="w-8 h-8" style={{ color: 'var(--ink3)', margin: '0 auto 12px' }} />
            <p style={{ fontSize: '13px', color: 'var(--ink2)' }}>{t('Brak spacerów do wyświetlenia')}</p>
          </div>
        ) : (
          <SpacerySection spacery={spacery} onRequestDeletion={handleRequestDeletion} />
        )}
      </main>

      <Footer fotograf={me.fotograf} />
    </div>
  );
}

// Wczytaj token z #preview_token=... gdy fotograf otworzył podgląd, i wyczyść hash.
// Oznacz tryb podglądu w sessionStorage, żeby pokazać baner.
function consumePreviewTokenFromHash() {
  if (!window.location.hash) return false;
  const m = window.location.hash.match(/preview_token=([^&]+)/);
  if (!m) return false;
  try {
    klientAuth.token = decodeURIComponent(m[1]);
    sessionStorage.setItem('sp_klient_preview', '1');
    // Zachowaj search params (np. ?lang=en) — wyczyść tylko hash z tokenem.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return true;
  } catch { return false; }
}

function isPreviewMode() {
  return sessionStorage.getItem('sp_klient_preview') === '1';
}

// ─── Root portal ─────────────────────────────────────────────────────────────
export default function KlientPortal({ slug }) {
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // Sprawdź preview token PRZED odczytem stanu auth — fotograf wchodzi z tokenem w hashu.
  const [authed, setAuthed] = useState(() => consumePreviewTokenFromHash() || !!klientAuth.token);
  const preview = isPreviewMode();

  useEffect(() => {
    klientApi.branding(slug)
      .then(setBranding)
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  const logout = () => {
    klientAuth.logout();
    sessionStorage.removeItem('sp_klient_preview');
    setAuthed(false);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{
          width: '32px', height: '32px',
          border: '3px solid var(--border)', borderTopColor: 'var(--acc)',
          borderRadius: '50%', animation: 'spin 0.8s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (err || !branding) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: '20px' }}>
        <div className="ds-card" style={{ padding: '32px', maxWidth: '400px', textAlign: 'center' }}>
          <IconAlert className="w-8 h-8" style={{ color: 'var(--err2)', margin: '0 auto 16px' }} />
          <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--ink)', marginBottom: '8px' }}>{t('Nie znaleziono panelu')}</p>
          <p style={{ fontSize: '11px', color: 'var(--ink3)' }}>
            {t('Sprawdź czy link jest poprawny.')} {err && `(${err})`}
          </p>
        </div>
      </div>
    );
  }

  if (authed) return <KlientPanel onLogout={logout} previewMode={preview} />;
  return <KlientLogin slug={slug} branding={branding} onLoggedIn={() => setAuthed(true)} />;
}
